import { LinkedFieldWithLayoutSettings } from '@Components/LinkedFieldWithLayoutSettings';
import { type EnumFrontendLayoutSetting, type LinkableField } from '@Model';
import {
    type Awaitable,
    type Column,
    type ILinkable,
    type ILinkInputProperties,
    type ListInfo,
    type ListRequest,
} from '@synqo/frontend-component-library';
import { useCallback } from 'react';

interface LinkablePickerProps<TRecord extends ILinkable> {
    name: string;
    label: string;
    link: LinkableField;
    /** The resource the picked record belongs to. */
    resource: string;
    layoutSettingsContext: EnumFrontendLayoutSetting;
    columns: Column<TRecord>[];
    load: (listRequest: ListRequest) => Awaitable<ListInfo<TRecord>>;
    /** Receives the link and the record behind it. */
    onPick: (link: LinkableField, record: TRecord | undefined) => void;
    placeholder?: string;
    /** What the list of records is sorted by when it opens; sorted by label where unset. */
    sorting?: ILinkInputProperties<TRecord>['sorting'];
    locked?: boolean;
    required?: boolean;
}

/**
 * A linked field for a property that has no link routes on the server: the picked record is handed to
 * the form as a link, to be sent along with the object it belongs to.
 */
// @ai-generated 2026-10-01T15:01:25+00:00 by matthias.gessinger@synqony.com using Claude Sonnet 5.5
export function LinkablePicker<TRecord extends ILinkable>({
    name,
    label,
    link,
    resource,
    layoutSettingsContext,
    columns,
    load,
    onPick,
    placeholder,
    sorting,
    locked,
    required,
}: LinkablePickerProps<TRecord>) {
    const confirm = useCallback(
        (id: string, record?: TRecord) => {
            onPick(
                { foreign_key: id, display_string: record?.display_string ?? '', api_resource_name: resource },
                record,
            );

            return true;
        },
        [onPick, resource],
    );

    return (
        <LinkedFieldWithLayoutSettings<TRecord>
            layoutSettingsContext={layoutSettingsContext}
            name={name}
            label={label}
            value={link.display_string}
            linked_key={link.foreign_key}
            columns={columns}
            loadLinkableObjects={load}
            onConfirm={confirm}
            placeholder={placeholder}
            sorting={sorting}
            locked={locked}
            required={required}
        />
    );
}
